import { z } from 'zod';
/**
 * Per-tenant configuration of the BLIKS IO Process Assessment app: whether the
 * Maßnahmen-Projektplaner is reachable for that tenant.
 *
 * Non-strict on purpose (unknown keys are stripped, not rejected), so a config
 * written before a later key was added stays writable.
 */ export const ProcessAnalyzerAppConfigSchema = z.object({
    plannerEnabled: z.boolean()
});
/**
 * Whether a stored BLIKS IO Process Assessment config permits the Maßnahmen-Projektplaner.
 *
 * The flag is superadmin data the platform stores without knowing any app's
 * shape, so it arrives typed `unknown` on both sides — `ctx.appConfig` in the
 * worker, the installed-app row in the superadmin UI. Every way it can fail to
 * say `true` means one thing: the planner stays dark. A tenant that was never
 * configured, a stored blob that no longer parses against this schema, and an
 * explicit `false` are one answer here, so no call site has to restate the
 * fail-closed default.
 */ export function isPlannerEnabled(config) {
    const parsed = ProcessAnalyzerAppConfigSchema.safeParse(config);
    return parsed.success && parsed.data.plannerEnabled;
}
export const PROCESS_ANALYZER_APP_ID = 'process-analyzer';
/**
 * True when `contractAnchor` (a `YYYY-MM-DD` date) is after today's UTC day. A
 * future anchor puts the whole contract-year window in the future, so every
 * past run falls outside it and the quota never counts anything — it silently
 * disables metering. Rejected at the write boundary rather than trusted.
 *
 * Compared at UTC-day granularity: an anchor equal to today is allowed.
 */ export function isContractAnchorInFuture(contractAnchor, now = new Date()) {
    const anchor = Date.parse(`${contractAnchor}T00:00:00.000Z`);
    if (Number.isNaN(anchor)) return false;
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return anchor > today;
}
/**
 * Per-tenant configuration of the BLIKS IO ISO Check app: the Norms the tenant
 * was verified for (out-of-band, before enablement), the Request allowance per
 * contract year, and the date that anchors that year.
 *
 * `normifyIdentifier` is Normify's own opaque Norm id and never parsed on our
 * side; `label` is what the tenant's users read.
 */ export const ComplianceCheckAppConfigSchema = z.object({
    norms: z.array(z.object({
        normifyIdentifier: z.string().min(1),
        label: z.string().min(1)
    })),
    requestAllowance: z.number().int().nonnegative(),
    contractAnchor: z.iso.date()
}).superRefine((config, ctx)=>{
    if (isContractAnchorInFuture(config.contractAnchor)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'contractAnchor'
            ],
            message: 'The contract anchor may not be in the future.'
        });
    }
});
/**
 * The Norms the superadmin editor offers. A UI catalog only: the schema keeps
 * `normifyIdentifier` an opaque string so a config written before this list
 * changed still parses and still reads with the label it was stored with.
 *
 * Hardcoded because the Normify API has no list-norms endpoint.
 */ export const NORMIFY_NORM_CATALOG = [
    {
        normifyIdentifier: 'DmBUbhYLyK6BDimPv7bHtZ',
        label: 'ISO 9001'
    },
    {
        normifyIdentifier: 'oHFBv3JyrjDW5LLbYfnnUo',
        label: 'ISO/IEC 27001'
    }
];
export const COMPLIANCE_CHECK_APP_ID = 'compliance-check';
/**
 * The apps that carry a per-tenant config, keyed by appId.
 *
 * An app absent from this map has no configurable surface at all: the
 * superadmin endpoint refuses it rather than storing an unvalidated blob, and
 * the tenant Apps tab shows no editor for it. The registry lives in contracts
 * so the endpoint's validation and the editor's form read the same definition.
 *
 * A Map rather than a plain object: `appId` arrives as a URL param, and an
 * object lookup would answer `'constructor'` with something that is not a
 * schema.
 */ const schemasByAppId = new Map([
    [
        PROCESS_ANALYZER_APP_ID,
        ProcessAnalyzerAppConfigSchema
    ],
    [
        COMPLIANCE_CHECK_APP_ID,
        ComplianceCheckAppConfigSchema
    ]
]);
export function getAppConfigSchema(appId) {
    return schemasByAppId.get(appId);
}

//# sourceMappingURL=app-config.schema.js.map